<script setup lang="ts">

const props = withDefaults(defineProps<{
    disabled?: boolean,
    modelValue: string[],
    placeHolder?: string,
    infoText?: string,
    hideIcon?: boolean,
    contained?: boolean,
    validators?:((val: string) => string | undefined)[]
}>(), {
    disabled: false,
    modelValue: () => { return []; },
    placeHolder: '',
    hideIcon: false,
    contained: false,
    validators: () => { return []; }
});

const emit = defineEmits(['update:modelValue', 'update:modified']);
const newTag = ref<string|null>(null);
const invalid = ref<boolean>(false);
const errorText = ref<string | undefined>();

watch(newTag, (val) => {
    if (val && val.length > 0) {
        emit('update:modified');
    }
});

const remove = (tag: string) => {
    emit('update:modelValue', props.modelValue.filter(t => t !== tag));
};


const resetError = () => {
    invalid.value = false;
    errorText.value = '';
};

const addNewTag = (): void => {
    if (!newTag.value || newTag.value.trim().length === 0) { return; }

    const newTagValue = newTag.value.trim();

    for (const validator of props.validators) {
        const error = validator(newTagValue);
        if (error) {
            invalid.value = true;
            errorText.value = error;
            return;
        }
    }

    emit('update:modelValue', [...props.modelValue, newTagValue]);
    newTag.value = null;
};

const discardInvalidDraft = (): void => {
    if (!newTag.value) {
        return;
    }

    const value = newTag.value.trim();
    if (props.validators.some(validator => Boolean(validator(value)))) {
        newTag.value = null;
        resetError();
    }
};

defineExpose({ discardInvalidDraft });
</script>

<template>
    <div
        class="flex flex-wrap gap-[8px]"
        :class="{
            'mt-[8px]': !props.contained,
            'tags-field-contained': props.contained
        }"
    >
        <VLChip
            v-for="(tag, index) in props.modelValue"
            :key="tag + index"
            :class="props.contained ? 'h-[30px]' : 'h-[38px]'"
            :removable="!props.disabled"
            @remove="remove(tag)"
        >
            {{ tag }}
        </VLChip>

        <div
            v-if="!props.disabled"
            class="flex flex-1"
            :class="props.contained ? 'items-center min-w-[180px]' : 'items-start min-w-[300px]'"
        >
            <div class="flex-1">
                <VLInputText
                    v-model="newTag"
                    :placeholder="props.placeHolder"
                    :invalid="invalid"
                    @keydown.enter="addNewTag"
                    @keydown="resetError"
                    @blur="addNewTag"
                />
                <small v-if="infoText" class="flex pt-[4px]">
                    {{ infoText }}
                </small>

                <div v-if="errorText" class="text-sm text-danger">
                    {{ errorText }}
                </div>
            </div>

            <span
                v-if="!props.hideIcon && !props.disabled"
                class="pi pi-plus ml-[8px] cursor-pointer mt-[12px]"
                @click="addNewTag"
            />
        </div>
    </div>
</template>

<style scoped>
    .tags-field-contained {
        align-items: center;
        background: var(--white-color);
        border: 1px solid var(--main-border-color-dark);
        border-radius: 4px;
        min-height: 38px;
        padding: 4px 8px;
    }

    .tags-field-contained:focus-within {
        border-color: var(--p-inputtext-focus-border-color);
    }

    .tags-field-contained :deep(.p-inputtext) {
        border: 0;
        box-shadow: none;
        padding-left: 0;
        padding-right: 0;
        width: 100%;
    }

    .tags-field-contained :deep(.p-inputtext:enabled:focus) {
        box-shadow: none;
    }
</style>
